<!--
  A header picker (navigation spec, piece 5): a compare's base or head, or
  a history's ref. The name stays what it was (a link to its history, on a
  compare), and a caret beside it ("Change base", or the page's key, `, b`
  / `, h`) turns the two into a field completing the repository's branches
  (a head's: a fork's too, `ana:feat`) through the jump bar's completion
  (jump/complete.ts over jump/pick.ts's sources); a choice goes to the
  subject it makes with `nav.go`, so Back returns. A name the list does not
  have is taken as the router reads it.

  The WAI-ARIA combobox with a listbox popup, as the jump bar's: the focus
  stays in the field (`aria-activedescendant`), the sources' sections are
  the listbox's groups, and each branch is one option. The popup is put in
  the body (it is no part of the heading the field sits in). The arrows
  move between branches; Enter takes the active one, waiting for a source
  that may replace a guess as the jump bar's Enter does (jump/enterWait),
  said in a polite region with a failed read; Esc gives the name back and
  the focus to the caret; the focus leaving closes it.
-->
<script lang="ts">
  import { tick, untrack } from 'svelte';
  import type { Attachment } from 'svelte/attachments';
  import { nav } from '../nav.svelte';
  import { Completer, flatRows, moveCursor, settleCursor, type Cursor, type Schedule, type Section } from '../jump/complete';
  import { EnterWait } from '../jump/enterWait.svelte';
  import { readBranches } from '../jump/github';
  import { pickCompletion, pickInitial, type PickAt, type PickCompletion } from '../jump/pick';
  import type { Row } from '../jump/rows';
  import { controlCommand } from '../keys/control';
  import { headerPickerKeys } from '../keys/controls';
  import { keyboard } from '../keys/keyboard.svelte';

  interface Props {
    /** The name shown (a commit's short SHA). */
    label: string;
    /** Where the picker is: its subject, the end it picks, and what the page carries. */
    pick: PickAt;
    /** The name's link, its history; null shows it as text (a history's own ref, a fork Changeling cannot name). */
    href?: string | null;
    /** Whether the field is open (the page opens it by key). */
    open?: boolean;
    /** What completes the text (jump/pick.ts's over GitHub by default). */
    completion?: PickCompletion;
    /** The debounce's and Enter's wait's clock (`setTimeout` by default). */
    schedule?: Schedule;
  }
  let { label, pick, href = null, open = $bindable(false), completion, schedule }: Props = $props();

  const timeout: Schedule = (run, ms) => {
    const t = setTimeout(run, ms);
    return () => clearTimeout(t);
  };
  const clock = untrack(() => schedule ?? timeout);
  const NOUN = { base: 'base', head: 'head', ref: 'branch' } as const;
  const noun = $derived(NOUN[pick.end]);
  const command = $derived({ kind: 'open', menu: pick.end === 'head' ? 'head' : 'base' } as const);
  const pickKey = $derived(keyboard.key(command));

  const uid = Math.random().toString(36).slice(2, 8);
  let text = $state('');
  let sections: Section[] = $state.raw([]);
  let asking = $state(false);
  let failed = $state(false);
  let cursor: Cursor | null = $state(null);
  /** The keys moved the cursor since the text changed: it stays on its row as the list changes. */
  let moved = false;
  let caret: HTMLButtonElement | undefined = $state();
  let field: HTMLInputElement | undefined = $state();
  let pop: HTMLElement | undefined = $state();
  let place = $state({ top: 0, left: 0, width: 320 });

  const rows = $derived(flatRows(sections));
  /** The branches listed (a name as typed is none). */
  const branches = $derived(rows.filter((r) => !r.provisional).length);
  const at = $derived.by(() => {
    if (!cursor) return null;
    const i = rows.findIndex((r) => r.id === cursor!.id);
    return i < 0 ? null : { i, row: rows[i] };
  });
  const optionId = (i: number) => `pick-${uid}-${i}`;
  const sectionId = (s: Section) => `pick-${uid}-${s.id.replace(/[^\w-]/g, '-')}`;
  const labelOf = (row: Row) => {
    const a = row.actions[0];
    return a.kind === 'go' ? a.label : '';
  };
  const named = (row: Row) => `${row.title}${row.detail ? ', ' + row.detail : ''}: ${labelOf(row)}`;

  let completer: Completer | null = null;
  let made = $state.raw<PickCompletion | null>(null);
  /** Enter's wait (jump/enterWait), as the jump bar's. */
  const wait = new EnterWait({
    schedule: untrack(() => clock),
    sure: () => !completer || completer.settled || (!!at && !at.row.provisional),
    flush: () => completer?.flush(),
    take: () => {
      if (at) take(at.row);
    },
  });

  /** The count, said when it changes: the branches, and a name as typed. */
  const counted = $derived.by(() => {
    if (!open) return '';
    const n = branches === 0 ? 'No branches' : branches === 1 ? '1 branch' : `${branches} branches`;
    return rows.length > branches ? `${n}; ${text.trim()} as typed` : n;
  });
  /** What the list's foot says: the wait, a read under way or failed, or why nothing is listed. */
  const foot = $derived(wait.foot || (asking ? 'Asking GitHub…' : failed ? 'The branches could not be read' : whyNone()));
  /** What the polite region says: the wait, or a failed read. */
  const said = $derived(open ? wait.said || (failed && !asking ? 'The branches could not be read' : '') : '');

  /** Why the list holds no branch for the text, once the sources answered. */
  function whyNone(): string {
    const m: PickCompletion | null = made;
    return m ? m.why(text, branches) : '';
  }

  function make(): Completer {
    made = completion ?? pickCompletion(() => pick, { read: readBranches });
    const c: Completer = new Completer({
      sources: made.sources,
      schedule: clock,
      onChange: () => {
        if (c !== completer) return;
        sections = c.sections;
        asking = !c.settled;
        failed = c.failed.length > 0;
        cursor = settleCursor(flatRows(sections), moved ? cursor : null);
        wait.changed(c.settled);
      },
    });
    return c;
  }

  const repoOf = () => ({ owner: pick.subject.owner, repo: pick.subject.repo });

  // Opened (by the caret, or the page's key): the name becomes the field, and the branches are asked for.
  $effect(() => {
    if (!open) return;
    untrack(() => {
      text = pickInitial(pick);
      cursor = null;
      moved = false;
      completer = make();
      completer.reset();
      completer.ask(text, repoOf());
    });
    void tick().then(() => {
      field?.focus();
      field?.setSelectionRange(text.length, text.length);
      position();
    });
    return () => {
      wait.stop();
      completer?.close();
      completer = null;
      sections = [];
    };
  });

  /** Below the field, inside the viewport's 16px sides. */
  function position() {
    if (!field) return;
    const r = field.getBoundingClientRect();
    const width = Math.min(420, Math.max(0, innerWidth - 32));
    place = { top: r.bottom + 4, left: Math.max(16, Math.min(r.left, innerWidth - width - 16)), width };
  }

  $effect(() => {
    if (!open) return;
    const on = () => position();
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => {
      window.removeEventListener('scroll', on, true);
      window.removeEventListener('resize', on);
    };
  });

  /** The popup in the body: it is no part of the heading the field is in. */
  const inBody: Attachment<HTMLElement> = (node) => {
    document.body.append(node);
    return () => node.remove();
  };

  // The active option in view: the focus stays in the field, so the browser does not scroll for it.
  $effect(() => {
    const i = at?.i;
    if (i === undefined) return;
    void tick().then(() => document.getElementById(optionId(i))?.scrollIntoView?.({ block: 'nearest' }));
  });

  /** Closed: the name is shown again; `refocus`, with the focus on its caret (Esc), else wherever the focus went. */
  function close(refocus: boolean) {
    wait.stop();
    open = false;
    if (refocus) void tick().then(() => caret?.focus());
  }

  function take(row: Row) {
    const a = row.actions[0];
    if (a.kind !== 'go') return;
    const here = location.pathname + location.search;
    close(true);
    if (a.href !== here) nav.go(a.href);
  }

  function oninput() {
    moved = false;
    wait.stop();
    completer?.ask(text, repoOf());
  }

  function onkeydown(e: KeyboardEvent) {
    const c = controlCommand(headerPickerKeys, e);
    if (!c) return;
    if (c === 'close') {
      // The field's own Esc: the router does not take it as leaving the field.
      e.preventDefault();
      e.stopPropagation();
      return close(true);
    }
    e.preventDefault();
    if (c === 'take') return wait.enter(text);
    if (!rows.length) return;
    wait.stop();
    cursor = moveCursor(rows, cursor, c === 'next' ? 'next-row' : 'prev-row');
    moved = true;
  }

  function onblur(e: FocusEvent) {
    const to = e.relatedTarget as Node | null;
    if (to && pop?.contains(to)) return;
    close(false);
  }

  function choose(i: number) {
    wait.stop();
    take(rows[i]);
  }
</script>

<span class="picker">
  {#if open}
    <input
      bind:this={field}
      bind:value={text}
      {oninput}
      {onkeydown}
      {onblur}
      class="picker-field"
      type="text"
      role="combobox"
      aria-label="Change the {noun}"
      aria-expanded={rows.length > 0}
      aria-controls="pick-{uid}-list"
      aria-haspopup="listbox"
      aria-autocomplete="list"
      aria-activedescendant={at ? optionId(at.i) : undefined}
      aria-busy={wait.finding ? 'true' : undefined}
      autocomplete="off"
      autocapitalize="off"
      spellcheck="false"
      placeholder={label}
      size={Math.max(12, Math.min(28, label.length + 2))}
    />
  {:else}
    {#if href}<a class="branch" {href} title="The history of {label}">{label}</a>{:else}<span class="branch">{label}</span>{/if}<button
      bind:this={caret}
      type="button"
      class="picker-caret"
      aria-label="Change {noun}"
      title="Change the {noun}{pickKey ? ` (${pickKey})` : ''}"
      onclick={() => (open = true)}><span aria-hidden="true">▾</span></button
    >
  {/if}
  <span class="sr-only" role="status">{counted}</span>
  <span class="sr-only picker-said" aria-live="polite">{said}</span>
</span>
{#if open}<!-- svelte-ignore a11y_no_static_element_interactions (a press inside keeps the focus in the field) --><div bind:this={pop} {@attach inBody} class="picker-pop" style="top:{place.top}px;left:{place.left}px;width:{place.width}px" onmousedown={(e) => e.preventDefault()}><div id="pick-{uid}-list" class="picker-list" role="listbox" aria-label="Branches">
      {#each sections as s (s.id)}
        <ul role="group" aria-labelledby={sectionId(s)}>
          <li role="presentation" id={sectionId(s)} class="picker-head">{s.title}</li>
          {#each s.rows as row (row.id)}
            {@const i = rows.indexOf(row)}
            <!-- svelte-ignore a11y_click_events_have_key_events (the field's keys choose an option: the focus never leaves the field) -->
            <li role="option" id={optionId(i)} tabindex="-1" class="picker-row" class:active={at?.i === i} aria-selected={at?.i === i} aria-label={named(row)} onclick={() => choose(i)}>
              <span class="picker-name">{row.title}</span>{#if row.detail}<span class="picker-detail">{row.detail}</span>{/if}
            </li>
          {/each}
        </ul>
      {/each}
    </div>
    {#if foot}<p class="picker-foot" class:picker-failed={failed && !wait.finding && !asking}>{foot}</p>{/if}</div>{/if}

<style>
  .picker {
    display: inline;
  }
  .picker-caret {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    min-height: 28px;
    margin-left: 2px;
    padding: 0;
    /* As the .btn.small controls beside it: seen at rest, so a name that can be changed shows it. */
    border: 1px solid var(--line);
    border-radius: 6px;
    background: var(--surface);
    color: var(--fg);
    font-size: 12px;
    line-height: 1;
    vertical-align: middle;
    cursor: pointer;
  }
  .picker-caret:hover,
  .picker-caret:focus-visible {
    border-color: var(--accent);
    background: var(--soft);
    color: var(--fg);
  }
  .picker-field {
    max-width: 100%;
    padding: 2px 6px;
    border: 1px solid var(--accent);
    border-radius: 6px;
    background: var(--surface);
    color: var(--fg);
    font: 0.8em ui-monospace, monospace;
    vertical-align: middle;
  }
  .picker-pop {
    position: fixed;
    z-index: 40;
    display: grid;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: var(--surface);
    box-shadow: 0 8px 24px rgb(0 0 0 / 0.18);
    font-size: 13px;
    font-weight: 400;
    letter-spacing: normal;
    overflow: hidden;
  }
  .picker-list {
    max-height: min(50vh, 360px);
    overflow: auto;
    padding: 4px 0;
  }
  .picker-list:empty {
    display: none;
  }
  .picker-list ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .picker-head {
    padding: 6px 12px 2px;
    font-size: 11px;
    font-weight: 600;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .picker-row {
    display: flex;
    gap: 8px;
    align-items: baseline;
    min-width: 0;
    padding: 6px 12px;
    cursor: pointer;
  }
  .picker-row.active {
    background: var(--soft);
    box-shadow: inset 2px 0 0 var(--accent);
  }
  .picker-name {
    flex: 0 1 auto;
    max-width: 75%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: 13px ui-monospace, monospace;
  }
  .picker-detail {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: var(--muted);
  }
  .picker-foot {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin: 0;
    padding: 6px 12px;
    border-top: 1px solid var(--line);
    font-size: 11px;
    color: var(--muted);
  }
  .picker-failed {
    color: var(--bad, var(--fg));
  }
</style>
